import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, Lock, ArrowLeft, RefreshCw, UserPlus, Trash2, Copy,
  KeyRound, Ticket, ClipboardList, FlaskConical, Mail, Send,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { adminUsersList, adminUserCreate, adminUserDelete, adminUserInvite } from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const fmtHu = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch { return iso; }
};

const genPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let p = '';
  for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
  return p;
};

const CreateForm = ({ adminKey, onCreated }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState(genPassword());
  const [withSample, setWithSample] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || password.length < 6) return;
    setBusy(true);
    try {
      await adminUserCreate(adminKey, {
        email: email.trim(), name: name.trim() || null,
        password, with_sample_data: withSample,
      });
      setCreated({ email: email.trim().toLowerCase(), password });
      toast.success('Fiók létrehozva.');
      setEmail(''); setName(''); setPassword(genPassword()); setWithSample(false);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Nem sikerült létrehozni a fiókot.');
    } finally { setBusy(false); }
  };

  const copyCreds = () => {
    const text = `Belépés: ${window.location.origin}/fiokom\nEmail: ${created.email}\nJelszó: ${created.password}`;
    navigator.clipboard.writeText(text).then(() => toast.success('Belépési adatok vágólapra másolva.'));
  };

  return (
    <div data-testid="admin-users-create-card" className="rounded-2xl border border-gray-800 bg-gray-900/60 p-5 md:p-6">
      <h2 className="font-bold text-white mb-4 flex items-center gap-2">
        <UserPlus size={18} className="text-yellow-500" /> Új fiók létrehozása
      </h2>
      <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-400 text-xs uppercase tracking-widest">Email *</Label>
          <Input data-testid="admin-users-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="tesztelo@email.hu" className="mt-1 bg-gray-950 border-gray-700 text-white" />
        </div>
        <div>
          <Label className="text-gray-400 text-xs uppercase tracking-widest">Név</Label>
          <Input data-testid="admin-users-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Teszt Elek" className="mt-1 bg-gray-950 border-gray-700 text-white" />
        </div>
        <div>
          <Label className="text-gray-400 text-xs uppercase tracking-widest">Jelszó * (min. 6 karakter)</Label>
          <div className="flex gap-2 mt-1">
            <Input data-testid="admin-users-password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="bg-gray-950 border-gray-700 text-white font-mono" />
            <Button type="button" variant="outline" size="icon" onClick={() => setPassword(genPassword())}
              title="Új jelszó generálása" className="border-gray-700 text-gray-300 hover:bg-gray-800 shrink-0">
              <KeyRound size={15} />
            </Button>
          </div>
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <Checkbox data-testid="admin-users-sample-checkbox" checked={withSample} onCheckedChange={(v) => setWithSample(!!v)}
              className="border-gray-500 data-[state=checked]:bg-yellow-600 data-[state=checked]:border-yellow-600 data-[state=checked]:text-gray-900" />
            <span className="text-sm text-gray-300">
              Mintaadatokkal <span className="text-gray-500 text-xs">(aktív bérlet + edzésterv A/B héttel + napló, mérések)</span>
            </span>
          </label>
        </div>
        <div className="md:col-span-2">
          <Button data-testid="admin-users-create-btn" type="submit" disabled={busy || !email.trim() || password.length < 6}
            className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
            {busy ? <Loader2 className="mr-2 animate-spin" size={15} /> : <UserPlus className="mr-2" size={15} />}
            Fiók létrehozása
          </Button>
        </div>
      </form>
      {created && (
        <div data-testid="admin-users-created-info" className="mt-4 rounded-xl border border-emerald-700/40 bg-emerald-500/10 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <div className="text-emerald-300 font-bold mb-1">Fiók kész — add oda a tesztelőnek:</div>
            <div className="text-gray-300">Belépés: <span className="font-mono">{window.location.origin}/fiokom</span></div>
            <div className="text-gray-300">Email: <span className="font-mono">{created.email}</span> · Jelszó: <span className="font-mono">{created.password}</span></div>
          </div>
          <Button data-testid="admin-users-copy-creds" variant="outline" size="sm" onClick={copyCreds}
            className="border-emerald-700/60 text-emerald-300 hover:bg-emerald-500/10">
            <Copy size={14} className="mr-1.5" /> Adatok másolása
          </Button>
        </div>
      )}
    </div>
  );
};

const InviteForm = ({ adminKey, onInvited }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // { email, link, email_status }
  const [resend, setResend] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await adminUserInvite(adminKey, {
        email: email.trim(), name: name.trim() || null,
        note: note.trim() || null, resend,
      });
      setSent({ email: email.trim().toLowerCase(), link: r.link, email_status: r.email_status });
      toast.success(r.email_status === 'sent'
        ? 'Meghívó elküldve — az ügyfél megkapja emailben.'
        : 'Meghívó link generálva. Az email küldés nem sikerült — másold ki a linket és küldd manuálisan.');
      setEmail(''); setName(''); setNote(''); setResend(false);
      onInvited();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Nem sikerült a meghívás.');
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!sent?.link) return;
    navigator.clipboard.writeText(sent.link).then(() => toast.success('Meghívó link vágólapra másolva.'));
  };

  return (
    <div data-testid="admin-users-invite-card" className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-5 md:p-6">
      <h2 className="font-bold text-white mb-2 flex items-center gap-2">
        <Mail size={18} className="text-blue-400" /> Vendég meghívása email-fiók regisztrációhoz
      </h2>
      <p className="text-xs text-gray-400 mb-4">
        Az ügyfél kap egy branded email-t egy „Fiók aktiválása" gombbal, amivel <strong>saját maga állítja be a jelszavát</strong>.
        A link 72 óráig érvényes. Ez az út azoknak való, akik <strong>nem szeretnének Google-lel</strong> regisztrálni.
      </p>
      <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-400 text-xs uppercase tracking-widest">Email *</Label>
          <Input data-testid="admin-users-invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="uj.vendeg@gmail.com" className="mt-1 bg-gray-950 border-gray-700 text-white" />
        </div>
        <div>
          <Label className="text-gray-400 text-xs uppercase tracking-widest">Név</Label>
          <Input data-testid="admin-users-invite-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Kovács Péter" className="mt-1 bg-gray-950 border-gray-700 text-white" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-gray-400 text-xs uppercase tracking-widest">Belső megjegyzés (csak neked, opcionális)</Label>
          <Input data-testid="admin-users-invite-note" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="pl. Konzultáció után egyeztetve, ajánlott heti 3× edzés"
            className="mt-1 bg-gray-950 border-gray-700 text-white" />
        </div>
        <div className="flex items-center pb-1">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <Checkbox data-testid="admin-users-invite-resend" checked={resend} onCheckedChange={(v) => setResend(!!v)}
              className="border-gray-500 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white" />
            <span className="text-sm text-gray-300">
              Újraküldés <span className="text-gray-500 text-xs">(felülír egy meglévő jelszavas fiók linkjét)</span>
            </span>
          </label>
        </div>
        <div className="md:col-span-2">
          <Button data-testid="admin-users-invite-btn" type="submit" disabled={busy || !email.trim()}
            className="bg-blue-600 hover:bg-blue-500 text-white font-bold">
            {busy ? <Loader2 className="mr-2 animate-spin" size={15} /> : <Send className="mr-2" size={15} />}
            Meghívó küldése
          </Button>
        </div>
      </form>
      {sent && (
        <div data-testid="admin-users-invited-info" className="mt-4 rounded-xl border border-blue-500/40 bg-blue-500/10 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm min-w-0 flex-1">
            <div className="text-blue-300 font-bold mb-1">
              Meghívó {sent.email_status === 'sent' ? 'elküldve' : 'link generálva (email nem ment ki)'}: <span className="font-mono">{sent.email}</span>
            </div>
            <div className="text-xs text-gray-400 font-mono truncate">{sent.link}</div>
          </div>
          <Button data-testid="admin-users-copy-invite-link" variant="outline" size="sm" onClick={copyLink}
            className="border-blue-500/60 text-blue-300 hover:bg-blue-500/10">
            <Copy size={14} className="mr-1.5" /> Link másolása
          </Button>
        </div>
      )}
    </div>
  );
};

const UserRow = ({ u, onDelete }) => (
  <div data-testid={`admin-user-row-${u.email}`} className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 flex flex-wrap items-center justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-bold text-white truncate">{u.email}</span>
        {u.is_test_account && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-purple-500/15 text-purple-300">
            <FlaskConical size={10} /> Teszt
          </span>
        )}
        {u.has_active_pass && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300">
            <Ticket size={10} /> Aktív bérlet
          </span>
        )}
        {u.plans_count > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-sky-500/15 text-sky-300">
            <ClipboardList size={10} /> {u.plans_count} terv
          </span>
        )}
      </div>
      <div className="text-xs text-gray-500 mt-1">
        {u.name || '—'} · Létrehozva: {fmtHu(u.created_at)} · Utolsó belépés: {fmtHu(u.last_login_at)}
        {' · '}{u.auth_providers?.includes('google') ? 'Google' : 'Jelszavas'}
      </div>
    </div>
    <Button data-testid={`admin-user-delete-${u.email}`} variant="outline" size="sm" onClick={() => onDelete(u)}
      className="border-red-900/60 text-red-400 hover:bg-red-500/10 hover:text-red-300">
      <Trash2 size={14} className="mr-1.5" /> Törlés
    </Button>
  </div>
);

export default function AdminUsersPage() {
  useDocumentMeta({ title: 'Fiókok — Admin | Orosz Coaching', noindex: true });
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [keyInput, setKeyInput] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (key) => {
    setLoading(true);
    try {
      setData(await adminUsersList(key));
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Érvénytelen admin kulcs.');
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else toast.error('Betöltési hiba.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (adminKey) load(adminKey); }, [adminKey, load]);

  const doDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const res = await adminUserDelete(adminKey, toDelete.email);
      toast.success(`Fiók törölve (${res.total} adatsor).`);
      setToDelete(null);
      load(adminKey);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A törlés nem sikerült.');
    } finally { setDeleting(false); }
  };

  if (!adminKey) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
        <form data-testid="admin-users-login"
          onSubmit={(e) => { e.preventDefault(); if (keyInput.trim()) { localStorage.setItem(ADMIN_KEY_STORAGE, keyInput.trim()); setAdminKey(keyInput.trim()); } }}
          className="w-full max-w-md bg-gray-800/80 rounded-2xl p-8 border border-gray-700 shadow-2xl">
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <UserPlus className="text-yellow-500" size={28} />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>Fiókok</h1>
          <Input data-testid="admin-users-key" type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
            placeholder="ADMIN_API_KEY" className="bg-gray-900 border-gray-700 text-white mb-4" autoFocus />
          <Button data-testid="admin-users-login-btn" type="submit" disabled={!keyInput.trim()}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
            <Lock className="mr-2" size={16} /> Belépés
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="admin-users-back" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <div className="w-10 h-10 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <UserPlus className="text-yellow-500" size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Fiókok</h1>
              <p className="text-xs text-gray-400">
                Teszt fiókok létrehozása és kezelése{data ? ` — ${data.total} fiók` : ''}
              </p>
            </div>
          </div>
          <Button data-testid="admin-users-refresh" variant="ghost" size="sm" onClick={() => load(adminKey)}
            className="text-gray-400 hover:text-white">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl space-y-6">
        <CreateForm adminKey={adminKey} onCreated={() => load(adminKey)} />
        <InviteForm adminKey={adminKey} onInvited={() => load(adminKey)} />

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-yellow-500" size={28} /></div>
        ) : (
          <div data-testid="admin-users-list" className="space-y-3">
            {(data?.users || []).map((u) => <UserRow key={u.user_id} u={u} onDelete={setToDelete} />)}
            {(data?.users || []).length === 0 && (
              <p className="text-center text-gray-500 py-10">Még nincs fiók.</p>
            )}
          </div>
        )}
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="admin-users-delete-dialog" className="bg-gray-900 border-gray-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Fiók végleges törlése</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              A(z) <span className="font-mono text-white">{toDelete?.email}</span> fiók és MINDEN hozzá tartozó adat
              (bérletek, foglalások, edzéstervek, naplók, mérések) véglegesen törlődik. Ez nem visszavonható.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white">Mégse</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-users-delete-confirm" onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? <Loader2 className="mr-2 animate-spin" size={14} /> : <Trash2 className="mr-2" size={14} />}
              Végleges törlés
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
